export default `
You will be given one worksheet JSON that contains at least one **Table** component with a tableData object. Your task is to produce a concise developer-facing guide explaining **exactly how to read user input values from tableData** at runtime (grading, feedback, analytics, replay, etc.).

Focus narrowly on **where the learner’s typed / constructed / saved numbers actually live** and how to reliably extract them. Do **not** discuss UI layout, styling, pedagogy, or how to render the table—only how to *read* the users attempt values.

Include the following sections, in order:

────────────────────────────────
### Reading User Input from tableData

#### 1. Locate tableData
• Give the full JSON path(s) where tableData appears in the worksheet (e.g., component.data.initial_table_data, runtime params.tableData, globals copies).  
• Note whether runtime functions receive params.tableData or pull a serialized copy from a global variable (e.g., global_context_variables.initial_table_data).

#### 2. Cell Object Schema (what to read)
Describe the structure of a single cell object in tableData.cells[row][col], listing only fields relevant to *user answers / attempt state*:
- text.enabled
- text.value.default
- fillColor.default (selection state)
- imageBackground.enabled / imageBackground.src.default (used for special buttons rows that may encode answers indirectly)
- tappable.tap_count.default (selection / de-selection info)
Mention any other field that must be checked before trusting a value (e.g., hidden rows, merged cells).

#### 3. Answer Column / Start Anchor
Identify the **coordinate anchor** that marks where saved answers begin (usually from a global such as answer_list_start = "r:c"). Explain how to parse the "r:c" string into numeric indices and how to iterate downward (row++) in that column to gather all user-saved responses until you reach a stopping condition (blank cell or disabled text).

#### 4. Reading a Single User Answer
Step list (pseudo):
1. Get ansRow, ansCol from the anchor.
2. Read cell = tableData.cells[ansRow][ansCol].
3. If cell.text.enabled === true **and** cell.text.value.default is a non-empty string, treat that as an answer token; push to results.
4. Advance row += 1 and repeat until you hit a cell that is missing, disabled, or empty.

Clarify that numeric conversion is optional; show how/when to Number(txt) and validate (finite, correct digit length, etc.) but *do not* perform task-specific grading—just extraction rules.

#### 5. Reading Multi-Digit Inputs Built from Selected Cells
If the interaction model lets the learner tap multiple digit cells and then press a Save control:
• Show how selected digit cells are detected (fillColor.default !== "#ffffff" or similar + tappable.tap_count.default > 0).
• Show how their text digits are ordered (left→right when in same row; top→bottom when in same column) before concatenation into a string that is later written into the answer column.
• Note any guardrails (must be collinear; abort if mixed rows/cols).

This section documents *how the Save logic builds the value* so downstream code reading the stored string knows what it represents.

#### 6. Special Rows That Affect Reading
List any special control rows (Save, Reset, Eliminate, Submit, etc.) and explain how to **ignore** them when scanning for answers. Use their identifying markers: coordinates from globals (e.g., save = "6:0"), image URLs (reset, eliminate_image), or text labels.

#### 7. Stop / Termination Conditions
Enumerate all reasons to stop reading further rows in the answer column:
- row out of bounds
- cell missing
- cell.text.enabled !== true
- cell.text.value.default === ""
- Optional: row cleared by elimination (all fields disabled)
Document recommended priority order when multiple conditions apply.

#### 8. Trust & Sanitization
Briefly specify what validation a consumer should apply after extraction:
• Trim whitespace.  
• Confirm string is numeric (or expected token format).  
• Optionally pad / reject if not required digit length.  
• Deduplicate if answers may repeat.

#### 9. Minimal Pseudocode
Provide ~10-20 lines of language-agnostic pseudocode readAnswers(tableData, anchorString) -> array<string> that implements the extraction spec above.

────────────────────────────────
Authoring rules
- Keep bullets terse; favor field paths (e.g., cells[r][c].text.value.default) over prose.
- Do not restate the entire worksheet JSON—summarize only the parts needed to read answers.
- If a field is absent in the given JSON, say “not present / ignore” rather than guessing.
- Output **only** the formatted guide above; no intro or closing remarks.

`;
